import { faChevronUp } from '@fortawesome/pro-regular-svg-icons/faChevronUp';
import Font from '@maxpreps/shared/styles/font';
import React, { ReactNode } from 'react';
import { DefaultTheme, useTheme } from 'styled-components';
import useHorizontalScrollHelpers from '../../hooks/useHorizonalScrollHelpers';
import { useIsMobile } from '../../hooks/useIsScreenSize';
import { IConfigurableLink } from '../../models/configurableLink';
import Icon from '../../ui/Icon/Icon';
import HoverOverflowMenu from '../../ui/OverflowMenu/HoverOverflowMenu';
import {
  LeftScrollShadow,
  RightScrollShadow
} from '../../ui/Shadows/ScrollShadow';
import SmartAnchor from '../Anchor/SmartAnchor';
import {
  NavBarContainer,
  NavBarLinkList,
  NavBarWrapper,
  StyledLeftContentContainer
} from './StyledPageNav';

export interface IPageNavLink {
  text: string;
  href: string;
  anchorComponent?: any;
  subItems?: IConfigurableLink[];
}

interface IPageNavProps {
  leftContent?: ReactNode;
  navId: string;
  pageNavLinks: IPageNavLink[];
}

interface INavItemProps {
  item: IPageNavLink;
  isMobile: boolean;
  theme: DefaultTheme;
  navId: string;
}

const NavItem: React.FC<INavItemProps> = ({ item, isMobile, theme, navId }) => {
  const { text, href, anchorComponent, subItems } = item;
  const AnchorComponent = href ? anchorComponent ?? SmartAnchor : 'div'; // For 'More' item
  const isActive = navId === text.toLowerCase();
  const portalId = `page_nav_${text}`;

  const link = (
    <AnchorComponent
      className={`link ${isMobile ? Font.f12_tall : Font.f14_tall}`}
      href={href}
    >
      {text}

      {subItems && <Icon icon={faChevronUp} size={12} rotation={180} />}
    </AnchorComponent>
  );

  const content = subItems ? (
    <HoverOverflowMenu
      $hoverColor={theme.colors.other.link}
      menuItems={subItems}
      menuHeader={link}
      portalId={portalId}
      ssr
    />
  ) : (
    link
  );

  return <li className={isActive ? 'active' : ''}>{content}</li>;
};

const PageNav: React.FC<IPageNavProps> = ({
  leftContent,
  pageNavLinks,
  navId
}) => {
  const isMobile = useIsMobile();
  const theme = useTheme();

  const [scrollElement, scrollXOffset, onScroll] = useHorizontalScrollHelpers();

  return (
    <NavBarWrapper>
      {scrollXOffset > 20 && <LeftScrollShadow />}

      <NavBarContainer ref={scrollElement} onScroll={onScroll}>
        {leftContent && (
          <StyledLeftContentContainer>{leftContent}</StyledLeftContentContainer>
        )}
        <NavBarLinkList>
          {pageNavLinks.map(item => (
            <NavItem
              item={item}
              isMobile={isMobile}
              theme={theme}
              navId={navId}
              key={`${item.text}-${item.href}`}
            />
          ))}
        </NavBarLinkList>
      </NavBarContainer>
      <RightScrollShadow />
    </NavBarWrapper>
  );
};

export default PageNav;
